// API Constants
import { encryptAES, decryptAES, CRYPTO_HEADERS } from '../utils/crypto';


const BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://www.admin-careplus.com/v1'
  : 'https://www.admin-careplus.com/v1';

// 암호화 사용 여부 설정
const USE_ENCRYPTION = true; // ⬅️ false로 하면 평문 모드
const MAX_RETRY_ATTEMPTS = 1; // 재시도 1번만


export { BASE_URL };

// ⭐ AUTH_EXPIRED 이벤트 발생 함수
const emitAuthExpired = () => {
  console.log('[API] AUTH_EXPIRED 이벤트 발생');
  window.dispatchEvent(new CustomEvent('auth:expired'));
};

//3YLmndhFNrefaa+D3Uqplw==  (개발)
// Common types from API docs
export interface Result {
  result: number; // 0 = success, 999 = fail
  message?: string;
  data?: any;
}

export interface PagingList {
  page: number;
  totalCnt: number;
}

// Authentication Types
export interface LoginReq {
  id: string;          // 이메일
  pw?: string;         // 비밀번호 (SNS 로그인 시 null)
  fcmId: string;       // FCM 토큰
  snsType: number;     // 0: 일반, 1: 구글, 2: 카카오, 3: 네이버
}

export interface AutoLoginReq {
  memberNo: number;
  apiKey: string;
  fcmId: string;
}

export interface LogoutReq {
  memberNo: number;
}

export interface LoginData {
  apiKey: string;
  memberNo: number;
  memberType: number;  // 0: 보호자, 1: 간병인
  name?: string;
  phone?: string;
}

// ⭐ 네이버 로그인 Types 추가
export interface NaverLoginReq {
  code: string;        // 네이버 인가 코드
  state: string;       // 네이버 state
  fcmId: string;       // FCM 토큰
}

// Phone Verification Types
export interface PhoneCertReq {
  phone: string;
}

export interface PhoneCertData {
  encNumber: string;  // 암호화된 인증번호
}

// ⭐ 구글 로그인 Types 추가
export interface GoogleLoginReq {
  code: string;        // 구글 인가 코드
  fcmId: string;       // FCM 토큰
}

// Member Registration Types
export interface RegMemberReq {
  phone: string;
  id: string;
  pw?: string;       // SNS 가입 시 null
  name: string;
  jumin: string;     // 주민번호 앞자리
  snsType: number;   // 0: 일반, 1: 구글, 2: 카카오, 3: 네이버
  fcmId: string;
}

// ⭐ Caregiver Registration Types
export interface RegMedicMemberReq {
  phone: string;
  id: string;
  pw?: string;
  name: string;
  gender: number;          // 0: 선택안함, 1: 남성, 2: 여성
  certificate?: string;    // 자격증
  areaData: AreaReq[];     // 서비스 가능 지역
  snsType: number;
  fcmId: string;
  memberPrFileNo?: number; // 프로필 이미지 파일번호
  jumin: string;           // 주민번호 13자리
  careOpt1: string,            // 돌봄강점 콤마구분
  careOpt2: string,            // 돌봄유형 콤마구분
}

export interface AreaReq {
  areaDataNo: number;
}

// ⭐ Area Search Types
export interface SearchAreaReq {
  searchKey: string;
}

export interface SearchAreaData {
  mainList: AreaData2[];
}

export interface AreaData2 {
  areaDataNo: number;
  sity: string;
  gungu: string;
}

// ⭐ Profile Upload Types
export interface UploadProfileData {
  memberPrFileNo: number;
}

// ID 중복 체크 Types
export interface CheckMemberIdReq {
  id: string;
}

// 간병 요청
export interface CareRequestReq {
  memberNo: number;
  apiKey: string;
  careWhat: number;
  careKind: number;
  matchType: string;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  careDays: string;
  careTime: number;
  sigunguCode: string;
  careArea: string;
  careAreaDetail: string;
  careRoom: string;
  xdVal: string;
  ydVal: string;
  ptName: string;
  ptHeight: string | null;
  ptWeight: string | null;
  ptAge: string | null;
  ptGender: number | null;
  ptRanking: number | null;
  ptMoveYn: number | null;
  ptDisease: string | null;
  ptOther: string | null;
  ptMon: string | null;
  ptDay: string | null;
  ptRelation: string | null;
  insuranceCompany: string | null; // ⭐ 가입 간병보험 회사명
  gdName: string | null;
  gdJuminNo: string | null;       // ⭐ 추가 간병인1 주민번호 (앞6+뒤1)
  gdPhone: string | null;
  gdRelation: string | null;
  gdName2: string | null;
  gdJuminNo2: string | null;      // ⭐ 추가 간병인2 주민번호 (앞6+뒤1)
  gdPhone2: string | null;
  gdRelation2: string | null;
  gdMemberName: string | null;
  gdMemberJuminNo: string | null;
  gdMemberPhone: string | null;
  ptCare: string | null;
  ptStatus: string | null;
  visitArea: string | null;       // ⭐ 추가 - 방문 장소 (병원동행만)
  visitAreaDetail: string | null; // ⭐ 추가 - 방문 상세주소 (병원동행만)
}

export interface CareRequestData {
  careNo?: number;  // 생성된 간병 요청 번호
  // 추가 응답 데이터가 있다면 여기에
}

// ⭐ Care Request Detail Types
export interface CareRequestDetailReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
}

export interface CareRequestDetailData {
  careNo: number;
  careWhat: number;
  careKind: number;
  matchType: string;        // ⭐ 추가
  oneDayMoney: number;      // ⭐ 추가
  careRoom: string;         // ⭐ 추가
  sigunguCode: string;      // ⭐ 추가
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  careArea: string;
  careAreaDetail: string;
  visitArea: string | null;       // ⭐ 추가
  visitAreaDetail: string | null; // ⭐ 추가
  careDays: string;
  period: number;
  careTime: number;
  xdVal: string;
  ydVal: string;
  ptName: string;
  ptBirth: string;
  ptHeight: string | null;
  ptWeight: string | null;
  ptAge: string;
  ptGender: number;
  ptRanking: number;
  ptMoveYn: number;
  ptDisease: string | null;
  ptOther: string | null;
  ptCare: string | null;
  ptStatus: string | null;
  ptRelation: string | null;
  gdMemberName: string | null;
  gdMemberJuminno: string | null;
  gdMemberPhone: string | null;
  insuranceCompany?: string | null;   // ⭐ 가입 간병보험 회사명
  isMyCareYn: number;
  familyList?: {                  // ⭐ 추가 (가족간병)
    gdNo: number;
    gdName: string;
    gdJuminNo?: string;           // ⭐ 주민번호 (앞6+뒤1)
    gdPhone: string;
    gdRelation: string;
  }[];
}

// ⭐ 공고 수정 요청 타입 추가
export interface ModifyCareRequestReq extends CareRequestReq {
  careNo: number;
}

// Profile Types
export interface ProfileRequest {
  memberNo: number;
  apiKey: string;  // ⭐ apiKey 추가
}

export interface GuardianProfile {
  name: string;
  phone: string;
  // 보호자 전용 필드 추가
}

export interface CaregiverProfile {
  name: string;
  phone: string;
  gender: number;
  certificateYn: number;
  imgPath?: string;
}

// ⭐ Estimate Write Types
export interface EstimateWriteReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
  careWhat?: number;
  oneDayMoney: number;
  message?: string;
}

export interface EstimateWriteRsp {
  estimateNo: number;
  careNo: number;
}

// ⭐ mdPayCheck Types (간병인 결제 가능 체크)
export interface MdPayCheckReq {
  memberNo: number;
  apiKey: string;
  estimateNo: number;
}

// 견적 수정
export interface EstimateUpdateReq {
  memberNo: number;
  apiKey: string;
  estimateNo: number;
  oneDayMoney: number;
  careWhat?: number;  // ⭐ 추가 (optional)
  message?: string;
}

// 견적 조회 (careNo로)
export interface EstimateListByCareNoReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
}

// 견적 삭제
export interface EstimateDeleteReq {
  memberNo: number;
  apiKey: string;
  estimateNo: number;
}

// 견적 상세 조회
export interface EstimateDetailReq {
  memberNo: number;
  apiKey: string;
  estimateNo: number;
}

export interface EstimateDetailData {
  estimateNo: number;
  careNo: number;
  memberNo: number;
  estimateState: number;
  createDate: string;
  updateDate: string;
  // 금액 정보
  oneDayMoney: number;
  totalMoney: number;
  period: number;
  estimateFee: number;
  subtotal: number;
  surtax: number;
  totalPay: number;
  // ⭐ 간병인 정보
  medicMemberNo: number;
  name: string;
  phone: string;
  gender: number;
  imgPath?: string;
  certificateYn: number;
  message?: string;
  // ⭐ 평점/리뷰
  rating: number;
  reviewCount: number;
  // ⭐ 간병 요청 정보
  ptName: string;
  startDate: string;
  endDate: string;
  careOpt1: string;    // 돌봄강점 콤마구분
  careOpt2: string;    // 돌봄유형 콤마구분
  certificate: string;
}

// ========== 메인화면 Types ==========

export interface MainViewReq {
  memberNo: number;
  apiKey: string;
}

export interface MainViewDataMainBn {
  title: string;
  imgPath: string;
  url: string;
}



export interface MainViewDataNotify {
  notifyNo: number;
  title: string;
  trRegDate: string;
}

export interface MainViewDataFaq {
  faqNo: number;
  title: string;
  trRegDate: string;
}


// ⭐ 보호자 메인 - 진행중 간병 Item
export interface GuardianMainCareItem {
  careNo: number;
  name: string;           // 간병인 이름
  careWhat: number;
  careKind: number;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  payType: number;
  payMoney: number;
}

// ⭐ 간병인 메인 - 진행중 간병 Item
export interface CaregiverMainCareItem {
  careNo: number;
  name: string;           // 보호자 이름
  careWhat: number;
  careKind: number;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  payType?: number;
  payMoney?: number;
}

// ⭐ 보호자 메인 Response
export interface GuardianMainViewData {
  mainBnList: MainViewDataMainBn[];
  mainList: GuardianMainCareItem[];
  notifyList: MainViewDataNotify[];
  faqList: MainViewDataFaq[];
}

// ⭐ 간병인 메인 Response
export interface CaregiverMainViewData {
  mainBnList: MainViewDataMainBn[];
  mainList: CaregiverMainCareItem[];
  notifyList: MainViewDataNotify[];
  faqList: MainViewDataFaq[];
}



// ⭐ 진행중 간병 상세 Types
export interface GoingCareDetailReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
}

// ⭐ 보조(추가) 간병인 = care_family (가족간병 careWhat=2 시 노출)
export interface CareFamilyVO {
  gdNo?: number;
  gdName: string;
  gdJuminNo?: string;    // 주민번호 (앞6+뒤1)
  gdPhone: string;
  gdRelation?: string;   // 환자와의 관계 (예: 딸, 배우자)
}

export interface GoingCareDetailData {
  calcYn?: number;
  careStatus: number;
  careNo: number;
  imgPath?: string;
  name: string;
  certificateYn: number;
  phone: string;
  gender: number;
  careWhat: number;
  careKind: number;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  careArea: string;
  careAreaDetail: string;
  ptAge: string;
  ptGender: number;
  ptRanking: number;
  ptMoveYn: number;
  ptDisease?: string;
  ptOther?: string;
  isMyCareYn: number;
  xdVal: string;
  ydVal: string;
  payReqYn: string;
  accNo: string;
  accNm: string;
  accExpireDt: string;
  rateMoney: number;
  careDays: string;
  // ⭐ 서명 필드 추가
  uuidFileName: string | null;
  fileType: string | null;
  path: string | null;
  realFileName: string | null;
  // ⭐ 가족간병(careWhat=2) 전용 필드
  ptName?: string;               // 환자 이름
  ptBirth?: string;              // 환자 생년월일 (YYYY-MM-DD)
  ptRelation?: string;           // (가족)주 간병인의 환자와의 관계
  familyList?: CareFamilyVO[];   // 보조(추가) 간병인 목록 (care_family)
}
// ⭐ care 테이블 서명 정보
export interface CareSignData {
  uuidFileName: string | null;
  fileType: string | null;
  path: string | null;
  realFileName: string | null;
}

// 보호자 간병 요청 목록 Request
export interface GdCareListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
  careState?: number;  // 0:진행/예정, 1:완료, 2:취소
}

// 보호자 간병 요청 목록 Response - Care Item
export interface GdCareListDataCare {
  careNo: number;
  careWhat: number;       // 0:단순간병, 1:전문간병, 2:가족간병
  careKind: number;       // 0:기간제, 1:시간제
  matchType: string;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  payReqYn: string;
  payNo: string;
  careArea: string;
  careAreaDetail: string;
  ptName: string;
  period: number;
  estimateCount: number;  // 받은 견적 개수
  careState: number;      // 0:진행/예정, 1:완료, 2:취소
  oneDayMoney: number;
  totalMoney: number;
  isConfirmed: number;    // ⭐ 추가: 0=미확정, 1=확정
}

// 보호자 간병 요청 목록 Response
export interface GdCareListData {
  pagingList: PagingList;
  careList: GdCareListDataCare[];
}

// ==================== ESTIMATE API ====================
// ⭐ 받은 견적 목록 Request
export interface LstesRcvdReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
  careNo: number;
}

// ⭐ 받은 견적 목록 Response - Estimate Item
export interface LstesRcvdDataEstimate {
  estimateNo: number;
  imgPath?: string;
  name: string;
  phone: string;
  certificateYn: number;
  gender: number;
  medicMemberNo: number;
  period: number;
  totalMoney: number;
  oneDayMoney: number;
  choiseYn: number;
  estimateState: number;
  message?: string;        // 지원 메시지
  appliedAt: string;       // 지원 시간
  rating: number;          // 평점
  reviewCount: number;     // 리뷰 수
  careWhat: number;       // 간병 종류
}

// ⭐ 받은 견적 목록 Response
export interface LstesRcvdData {
  pagingList: PagingList;
  estimateList: LstesRcvdDataEstimate[];
}



// ==================== REVIEW API ====================

// 리뷰 아이템
export interface ReviewVO {
  reviewNo: number;
  score: number;
  content: string;
  regDate: string;
  reviewerName: string;
  regMemberNo: number;
  reMemberNo: number;
  deleteYn?: number;
}

// 간병인 리뷰 목록 Request
export interface MedicReviewListReq {
  memberNo: number;
  apiKey: string;
  medicMemberNo: number;  // 조회할 간병인 번호
}

// 간병인 리뷰 목록 Response
export interface MedicReviewListData {
  reviewList: ReviewVO[];
}


// ==================== PAID (정산계좌) API ====================

// 환급계좌 조회 Request
export interface DetailMdBankReq {
  memberNo: number;
  apiKey: string;
}

// 환급계좌 조회 Response
export interface DetailMdBankData {
  bankName: string | null;
  bankHolder: string | null;
  bankNumber: string | null;
}

// 환급계좌 등록/수정 Request
export interface ModPaidBankInfoReq {
  memberNo: number;
  apiKey: string;
  bankName: string;
  bankHolder: string;
  bankNumber: string;
}


// ==================== NOTIFICATION API TYPES ====================

// ⭐ 알림 목록 Request
export interface NotiListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

// ⭐ 알림 아이템
export interface NotiListDataNoti {
  notiNo: number;
  memberNo?: number;
  type: number;          // 알림 타입 (백엔드는 type 사용)
  message: string;       // 알림 메시지 (HTML 포함)
  readYn: string;        // 읽음 여부 ("0": 안읽음, "1": 읽음) - 백엔드는 문자열
  refNo: number;         // 참조 번호 (careNo, estimateNo 등)
  keywordOne?: string;   // 키워드1
  keywordTwo?: string;   // 키워드2
  regDate: string;       // 포맷된 등록일시 (예: "1시간 전")
}

// ⭐ 알림 목록 Response
export interface NotiListData {
  notiList: NotiListDataNoti[];
  pagingList: PagingList;
}

// ⭐ 알림 읽음 처리 Request
export interface NotiCheckOneReq {
  memberNo: number;
  apiKey: string;
  notiNo: number;
}

// ⭐ 알림 전체 읽음 Request
export interface NotiCheckGroupReq {
  memberNo: number;
  apiKey: string;
}

// ⭐ 내 알림 체크 Response
export interface CheckMyNotiData {
  myNotiCheck: number;  // 0: 읽지 않은 알림 없음, 1: 읽지 않은 알림 있음
}


// ==================== CARE NOTE API ====================

// 간병일지 작성 Request
export interface CareNoteWriteReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
  noteDate: string;         // YYYYMMDD
  noteHour: number;         // 0-23
  content: string;          // JSON 문자열
  careNoteFileNoList?: number[];  // ⭐ number[] (수정)
}

// 간병일지 작성 Response
export interface CareNoteWriteData {
  careNoteNo: number;
}

// 간병일지 수정 Request
export interface CareNoteUpdateReq {
  memberNo: number;
  apiKey: string;
  careNoteNo: number;
  noteDate: string;
  noteHour: number;
  careNoteFileNoList?: CareNoteFileNoItem[];
}

export interface CareNoteFileNoItem {
  careNoteFileNo: number;
}

// 간병일지 목록 Request
export interface CareNoteListReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
  page: number;
  perPageNum: number;
}

// 간병일지 상세 Request
export interface CareNoteDetailReq {
  memberNo: number;
  apiKey: string;
  careNoteNo: number;
}

// 간병일지 삭제 Request
export interface CareNoteDeleteReq {
  memberNo: number;
  apiKey: string;
  careNoteNo: number;
}

// 간병일지 목록 Item
export interface CareNoteListItem {
  careNoteNo: number;
  careNo: number;
  noteDate: string;      // YYYYMMDD
  noteHour: number;      // 0-23
  content: string;       // ⭐ 추가
  regDate: string;
  updateDate: string;
  imgPath?: string;  // ⭐ 추가
}

// 간병일지 목록 Response
export interface CareNoteListData {
  pagingList: PagingList;
  careNoteList: CareNoteListItem[];
}

// 간병일지 상세 Response
export interface CareNoteDetailData {
  careNoteNo: number;
  careNo: number;
  noteDate: string;
  noteHour: number;
  content: string;
  regDate: string;
  updateDate: string;
}

// 간병일지 파일 정보
export interface CareNoteFileData {
  careNoteFileNo: number;
  realFileName: string;
  path: string;
  uuidFileName: string;
  fileType: 'signature' | 'image';
  deleteYn: number;
  regDate: string;
  filePath: string;  // ⭐ 전체 경로 (백엔드에서 CONCAT으로 생성)
}

// 간병일지 상세 전체 Response (파일 포함)
export interface CareNoteDetailFullData {
  mainData: CareNoteDetailData;
  fileList: CareNoteFileData[];
}

// 간병일지 파일 업로드 Response
export interface CareNoteFileUploadData {
  careNoteFileNoList: Array<{
    careNoteFileNo: number;
  }>;
}

// ⭐ 간병일지 체크리스트 구조 (content JSON 파싱용)
export interface CareNoteContent {
  식사보조?: string[];
  활동보조?: string[];
  배변보조?: string[];
  위생보조?: string[];
  기타보조?: string;
}

// 간병일지 진행중인 간병 목록 Request
export interface MedicCareGoingListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

// 간병일지 진행중인 간병 Item
export interface MedicCareGoingItem {
  careNo: number;
  ptName: string;
  careWhat: number;
  careKind: number;
  startDate: string;
  endDate: string;
  careArea: string;
}

// 간병일지 진행중인 간병 목록 Response
export interface MedicCareGoingListData {
  pagingList: PagingList;
  careList: MedicCareGoingItem[];
}

// ==================== CERTIFICATE API (보험서류) ====================

// 증명서 목록 조회 Request
export interface GetCertificateListReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
}

// 증명서 아이템
export interface CertificateItem {
  CERTIFICATE_NO: number;
  CARE_NO: number;
  ESTIMATE_NO: number;
  PAY_NO: number | null;
  GD_MEMBER_NO: number;
  MD_MEMBER_NO: number | null;
  CERTIFICATE_TYPE: number;          // 1:간병확인서, 2:회원등록확인서, 3:신용카드매출전표, 4:간병일지, 6:신용카드취소전표
  CERTIFICATE_TYPE_NAME: string;     // 한글명
  FILE_PATH: string;
  FILE_NAME: string;
  DOWNLOAD_COUNT: number;
  SEND_COUNT: number;
  REG_DATE: string;
  LAST_DOWNLOAD_DATE: string | null;
}

// 증명서 목록 조회 Response
export interface GetCertificateListData {
  certificateList: CertificateItem[];
}


// ==================== 간병인 일감 찾기 Types ====================
export interface MemberPagingReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

export interface JobSearchDataCare {
  careNo: number;
  careWhat: number;
  careKind: number;
  matchType: string;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  careArea: string;
  careAreaDetail: string;
  oneDayMoney: number;
  totalMoney: number;
}

export interface JobSearchData {
  pagingList: PagingList;
  careList: JobSearchDataCare[];
}

export interface CareReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
}

export interface JobSearchDetailData {
  name: string;
  phone: string;
  careWhat: number;
  careKind: number;
  matchType: string;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  careArea: string;
  careAreaDetail: string;
  ptName: string;
  ptHeight: string;
  ptWeight: string;
  ptAge: number;
  ptBirth: string;
  ptGender: number;
  ptRanking: number;
  ptMoveYn: number;
  ptDisease: string;
  ptOther: string;
  estimateYn: number;
  estimateNo?: number;
  xdVal: string;
  ydVal: string;
  ptCare?: string;
  ptStatus?: string;
  careDays: string;
  oneDayMoney: number;
  totalMoney: number;
  visitArea?: string;       // ⭐ 추가 - 방문 장소 (병원동행만)
  visitAreaDetail?: string; // ⭐ 추가 - 방문 상세주소 (병원동행만)
  message?: string;        // 지원 메시지 (견적 작성 시)
}

export interface EstimateReq {
  memberNo: number;
  apiKey: string;
  estimateNo: number;
}

export interface MedicEstimateDetailData {
  oneDayMoney: number;
}

export interface MedicEstimateModReq {
  memberNo: number;
  apiKey: string;
  estimateNo: number;
  oneDayMoney: number;
}

// ==================== 보낸견적 Types ====================
export interface RgEstimateListDataRgEstimate {
  estimateNo: number;
  careNo: number;
  oneDayMoney: number;
  totalMoney: number;
  regDate: string;
  careWhat: number;
  careKind: number;
  startDate: string;
  endDate: string;
  careArea: string;
  careAreaDetail: string;
  startCareTime: string;
  endCareTime: string;
}

export interface RgEstimateListData {
  rgEstimateList: RgEstimateListDataRgEstimate[];
  pagingList: PagingList;
}

export interface RegInquiryReq {
  memberNo: number;
  apiKey: string;
  email: string;
  title: string;      // category 값
  content: string;
}


export interface OutMemberActReq {
  memberNo: number;
  apiKey: string;
  deleteReason: string;
}

// ========== 정산 관련 Types ==========
export interface MemberPagingReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

export interface PaidListReq extends MemberPagingReq { }

export interface PaidListItem {
  paidNo: number;
  paidYn: number;      // 0: 정산 예정, 1: 정산 완료
  name: string;        // 환자(보호자) 이름
  startDate: string;   // 간병 시작일 (yyyy.MM.dd)
  endDate: string;     // 간병 종료일 (yyyy.MM.dd)
  totalMoney: number;  // 총 간병비
}

export interface PaidListData {
  totalPaid: number;           // 총 정산 완료 금액 (PAID_YN=1인 것들의 REFUND_AMOUNT 합계)
  paidList: PaidListItem[];    // 정산 목록
  pagingList: PagingList;      // 페이징 정보
}

export interface PaidDetailReq {
  memberNo: number;
  apiKey: string;
  paidNo: number;
}

export interface PaidDetailData {
  oneDayMoney: number;   // 일일 간병비
  totalMoney: number;    // 총 간병비
  period: number;        // 간병 기간(일)
  paidFee: number;       // 수수료 (가족간병 10% / 그 외 3%)
  insrnFee: number;      // 보험료 (950원)
  subtotal: number;      // 소계
  withholding: number;   // 원천징수 (3.3%, 가족간병은 면제)
  refundAmount: number;  // 정산 금액
  careWhat: number;      // 간병유형 (0:단순, 1:전문, 2:가족간병, 3:병원동행)
}

// ========== FAQ 관련 Types ==========
export interface FaqListReq extends MemberPagingReq { }

export interface FaqListItem {
  faqNo: number;
  title: string;
}

export interface FaqListData {
  faqList: FaqListItem[];
  pagingList: PagingList;
}

// 공지사항 타입 추가
export interface NotifyListReq extends MemberPagingReq { }

export interface NotifyListItem {
  notifyNo: number;
  title: string;
  regDate: string;
}

export interface NotifyListData {
  notifyList: NotifyListItem[];
  pagingList: PagingList;
}



// ========== 프로필 관련 Types ==========
// 보호자 프로필 상세
export interface GdMemberPrDetailData {
  name: string;
  jumin: string;
}

// 간병인 프로필 상세
export interface MdMemberPrDetailData {
  name: string;
  gender: number;
  certificate: string;
  imgPath: string;
  memberPrFileNo: number | null;
  areaData: AreaData2[];
  jumin: string | null;
  careOpt1: string;    // 돌봄강점 콤마구분
  careOpt2: string;    // 돌봄유형 콤마구분
}

// 보호자 프로필 수정 요청
export interface ModGdMemberPrReq {
  memberNo: number;
  apiKey: string;
  name: string;
}

// 간병인 프로필 수정 요청
export interface ModMdMemberPrReq {
  memberNo: number;
  apiKey: string;
  name: string;
  gender: number;
  certificate?: string;
  areaData: { areaDataNo: number }[];
  memberPrFileNo?: number;
  jumin: string;
  careOpt1: string;    // 돌봄강점 콤마구분
  careOpt2: string;    // 돌봄유형 콤마구분
}

// 비밀번호 변경 요청
export interface ChangeMyProPwReq {
  memberNo: number;
  apiKey: string;
  pw: string;      // 현재 비밀번호
  newPw: string;   // 새 비밀번호
}

// 연락처 변경 - 인증번호 요청
export interface PhoneCertChangePhoneNumReq {
  memberNo: number;
  phone: string;
}

// 연락처 변경 - 인증번호 응답
export interface PhoneCertChangePhoneNumData {
  encNumber: string;
}

// 연락처 변경 - 최종 변경
export interface ChangePhoneNumReq {
  memberNo: number;
  apiKey: string;
  phone: string;
}


// ==================== 출퇴근 관리 (지오펜스) API ====================

export interface ActiveCare {
  careNo: number;
  careArea: string;
  careAreaDetail: string;
  xdVal: string;          // 경도
  ydVal: string;          // 위도
  geoRadius: number;      // 지오펜스 반경 (미터)
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  ptName: string;
  careKind: number;
  currentAttendNo?: number;  // 현재 출근 중이면 값 존재
  currentInTime?: string;
}

export interface ActiveCareListData {
  result: string;
  data: ActiveCare[];
}

export interface CheckInReq {
  careNo: number;
  lat: string;
  lng: string;
}

export interface CheckInData {
  result: string;
  data: {
    success: boolean;
    message: string;
    attendNo?: number;
    inTime?: string;
  };
}

export interface CheckOutReq {
  attendNo: number;
  lat: string;
  lng: string;
}

export interface CheckOutData {
  result: string;
  data: {
    success: boolean;
    message: string;
    outTime?: string;
    workHour?: number;
  };
}

export interface AttendHistoryReq {
  startDate?: string;  // yyyy-MM-dd
  endDate?: string;    // yyyy-MM-dd
  careNo?: number;
}

export interface AttendRecord {
  attendNo: number;
  careNo: number;
  careArea: string;
  careAreaDetail: string;
  ptName: string;
  inTime: string;
  outTime?: string;
  workHour?: number;
  workDate: string;
  checkInTime: string;
  checkOutTime?: string;
}

export interface AttendHistoryData {
  result: string;
  data: AttendRecord[];
}

export interface TodayAttendReq {
  careNo: number;
}

export interface TodayAttend {
  attendNo: number;
  careNo: number;
  memberNo: number;
  inTime: string;
  outTime?: string;
  workHour?: number;
  checkInTime: string;
  checkOutTime?: string;
  status: 'WORK' | 'DONE';  // 근무중 | 퇴근완료
}

export interface TodayAttendData {
  result: string;
  data: TodayAttend | null;
  msg?: string;
}

// ============================================
// 간병인 이용내역 (보호자)
// ============================================

export interface MemberUseMdListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

export interface MemberUseMdListDataCare {
  careNo: number;
  name: string;
  careKind: number;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  refundYn: number;
  medicMemberNo: number;        // 추가
  reviewNo: number | null;      // 추가
  score: number | null;         // 추가
  reviewContent: string | null; // 추가
  reviewRegDate: string | null; // 추가
}

export interface MemberUseMdListData {
  careList: MemberUseMdListDataCare[];
  pagingList: {
    page: number;
    totalCnt: number;
  };
}

// ============================================
// 간병 내역 (간병인)
// ============================================

export interface MemberRunCareListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

export interface MemberRunCareListDataCare {
  careNo: number;
  name: string;           // 보호자 이름
  careKind: number;       // 0: 기간제, 1: 시간제, 2: 24시간제
  startDate: string;      // YYYY.MM.DD
  endDate: string;        // YYYY.MM.DD
  startCareTime: string;  // HH:mm
  endCareTime: string;    // HH:mm
  refundYn: number;       // 0: 환급대기, 1: 환급완료
}

export interface MemberRunCareListData {
  careList: MemberRunCareListDataCare[];
  pagingList: {
    page: number;
    totalCnt: number;
  };
}

// ============================================
// 간병 상세 (간병인)
// ============================================

export interface MedicGoingCareDetailData {
  name: string;           // 보호자 이름
  phone: string;
  careWhat: number;
  careKind: number;
  startDate: string;
  endDate: string;
  startCareTime: string;
  endCareTime: string;
  careArea: string;
  careAreaDetail: string;
  ptName: string;
  ptHeight: string;
  ptWeight: string;
  ptAge: number;
  ptBirth: string;
  ptGender: number;
  ptRanking: number;
  ptMoveYn: number;
  ptDisease: string | null;
  ptOther: string | null;
  xdVal: string;
  ydVal: string;
  isEstimateYn: number;
  careStatus: number;
  calcYn: number | null;  // ⭐ 정산 상태
  ptCare: string | null;
  ptStatus: string | null;
  careDays: string;
}

// ⭐ 간병인 내 간병 목록 Request (선택된 견적 기준)
export interface MedicCareChoListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
  careState?: number;  // 0:진행/예정, 1:완료 (간병인은 취소 탭 없음)
}

// ⭐ 간병인 내 간병 목록 Response (GdCareListDataCare 재사용)
export interface MedicCareChoListData {
  pagingList: PagingList;
  careList: GdCareListDataCare[];
}


// ==================== 아이디/비밀번호 찾기 Types ====================

// 아이디 찾기 - 전화번호 인증 요청
export interface PhoneCertFindIdReq {
  phone: string;
}

// 아이디 찾기 - 전화번호 인증 응답
export interface PhoneCertFindIdData {
  encNumber: number;
  memberNo: number;
}

// 아이디 찾기 - 조회 요청
export interface FindMemberIdReq {
  memberNo: number;
}

// 아이디 찾기 - 조회 응답
export interface FindMemberIdData {
  id: string;
  regDate: string;
}

// 비밀번호 찾기 - 이메일 확인 요청
export interface CheckMemberIdFindPwReq {
  id: string;
}

// 비밀번호 찾기 - 전화번호 인증 요청
export interface PhoneCertFindPwReq {
  id: string;
  phone: string;
}

// 비밀번호 찾기 - 전화번호 인증 응답
export interface PhoneCertFindPwData {
  encNumber: number;
  memberNo: number;
}

// 비밀번호 변경 요청
export interface ChangeMemberPwReq {
  memberNo: number;
  pw: string;
}


// ==================== 리뷰 Types ====================

// 간병인 이용내역 리뷰 목록 아이템 (보호자용)
export interface MedicReviewListItem {
  reviewNo: number;
  regMemberNo: number;
  name: string;
  score: number;
  content: string;
  isMyReviewYn: number;   // 1: 내가 쓴 리뷰
  regDate: string;
  isReviewSingoYn: number;
}

// 간병인 리뷰 목록 조회 (보호자 → 특정 간병인 리뷰 목록)
export interface MedicMemberDetailReviewListReq {
  memberNo: number;
  apiKey: string;
  medicMemberNo: number;
  page: number;
  perPageNum: number;
}

export interface MedicMemberDetailReviewListData {
  reviewList: MedicReviewListItem[];
  pagingList: PagingList;
}

// 리뷰 등록 요청 (보호자)
export interface RegReviewReq {
  memberNo: number;
  apiKey: string;
  medicMemberNo: number;
  careNo: number;
  score: number;
  content: string;
}

// 리뷰 수정 요청
export interface ModReviewReq {
  memberNo: number;
  apiKey: string;
  reviewNo: number;
  score: number;
  content: string;
}

// 리뷰 삭제 요청
export interface DeleteReviewReq {
  memberNo: number;
  apiKey: string;
  reviewNo: number;
}

// 리뷰 상세 조회 요청 (수정용)
export interface DetailReviewReq {
  memberNo: number;
  apiKey: string;
  reviewNo: number;
}

export interface DetailReviewData {
  reviewNo: number;
  score: number;
  content: string;
}

export interface MemberUseMdListReq {
  memberNo: number;
  apiKey: string;
  page: number;
  perPageNum: number;
}

export interface DelCareReqReq {
  memberNo: number;
  apiKey: string;
  careNo: number;
}

// ⭐ 간병일지 파일 업로드 함수
async function uploadCareNoteFile(
  file: File,
  fileType: 'image' | 'signature',
  careNo?: number   // ⭐ 추가
): Promise<CareNoteFileUploadData> {
  try {
    const formData = new FormData();
    formData.append('imgFile', file);
    formData.append('fileType', fileType);
    if (careNo !== undefined) {
      formData.append('careNo', String(careNo));  // ⭐ 추가
    }
    const response = await fetch(`${BASE_URL}/care/regCareNoteSign.do`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    let responseText = await response.text();

    // JSON 문자열 언랩
    if (responseText.startsWith('"') && responseText.endsWith('"')) {
      try {
        responseText = JSON.parse(responseText);
      } catch (unwrapError) {
        console.error('[API] JSON 문자열 언랩 실패:', unwrapError);
      }
    }

    // 복호화
    if (USE_ENCRYPTION && responseText && !responseText.startsWith('{')) {
      try {
        responseText = decryptAES(responseText);
      } catch (decryptError) {
        throw new Error('응답 복호화에 실패했습니다.');
      }
    }

    // JSON 파싱
    let result: Result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error('응답 파싱에 실패했습니다.');
    }

    if (result.result !== 0) {
      const errorMessage = getErrorMessage(result.result);
      throw new Error(errorMessage);
    }

    return result.data as CareNoteFileUploadData;
  } catch (error) {
    throw error;
  }
}




// Generic API Request Function
async function apiRequest<T>(endpoint: string, data: object, retryCount = 0): Promise<T> {

  try {

    const bodyText = JSON.stringify(data);
    let requestBody: string;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // ========== 요청 암호화 ==========
    if (USE_ENCRYPTION) {
      requestBody = encryptAES(bodyText);
      headers['x-crypt-apigw-enable'] = CRYPTO_HEADERS.ENABLED;
    } else {
      requestBody = bodyText;
      console.log('[API] 평문 요청 전송');
    }

    // ========== HTTP 요청 ==========
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: requestBody,
      credentials: 'include',
    });


    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // ========== 응답 처리 ==========
    let responseText = await response.text();

    // ⭐ 1단계: JSON 문자열인 경우 파싱 (큰따옴표 제거)
    if (responseText.startsWith('"') && responseText.endsWith('"')) {
      try {
        responseText = JSON.parse(responseText);
        console.log('====================');
        console.log(responseText);
      } catch (unwrapError) {
        console.error('[API] JSON 문자열 언랩 실패:', unwrapError);
      }
    }

    // ⭐ 2단계: 암호화된 응답 복호화
    if (USE_ENCRYPTION && responseText && !responseText.startsWith('{')) {

      try {
        responseText = decryptAES(responseText);

      } catch (decryptError) {
        throw new Error('응답 복호화에 실패했습니다.');
      }
    }

    // ⭐ 3단계: JSON 파싱
    let result: Result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error('응답 파싱에 실패했습니다.');
    }

    // ========== 결과 검증 ==========
    if (result.result !== 0) {


      const errorMessage = getErrorMessage(result.result);

      // ⭐ result: 1000 (인증 만료/다른 기기 로그인) 특별 처리
      if (result.result === 1000) {


        console.log('[API] 인증 만료 감지');

        // 재시도 가능한 경우
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          console.log('[API] 자동 재인증 시도...');

          try {
            const userData = localStorage.getItem('user');
            const fcmId = localStorage.getItem('fcmId') || '';

            console.log('[API] localStorage 체크:', {
              hasUser: !!userData,
              hasFcmId: !!fcmId
            });

            if (userData) {
              const user = JSON.parse(userData);

              console.log('[API] 재인증 요청:', {
                memberNo: user.memberNo,
                hasApiKey: !!user.apiKey
              });

              const autoLoginData = {
                memberNo: user.memberNo,
                apiKey: user.apiKey,
                fcmId: fcmId
              };


              const autoLoginBody = USE_ENCRYPTION
                ? encryptAES(JSON.stringify(autoLoginData))
                : JSON.stringify(autoLoginData);

              const headers: Record<string, string> = {
                'Content-Type': 'application/json',
              };
              if (USE_ENCRYPTION) {
                headers['x-crypt-apigw-enable'] = CRYPTO_HEADERS.ENABLED;
              }

              const loginResponse = await fetch(`${BASE_URL}/member/autoLogin.do`, {
                method: 'POST',
                headers: headers,
                body: autoLoginBody,
                credentials: 'include',
              });

              console.log('[API] 재인증 응답 상태:', loginResponse.status);

              if (loginResponse.ok) {

                let loginResponseText = await loginResponse.text();

                // JSON 문자열 언랩
                if (loginResponseText.startsWith('"') && loginResponseText.endsWith('"')) {
                  loginResponseText = JSON.parse(loginResponseText);
                }

                // 복호화
                if (USE_ENCRYPTION && loginResponseText && !loginResponseText.startsWith('{')) {
                  loginResponseText = decryptAES(loginResponseText);
                }

                const loginResult = JSON.parse(loginResponseText);

                console.log('[API] 재인증 결과:', loginResult.result);

                if (loginResult.result === 0) {
                  console.log('[API] ✅ 자동 재인증 성공 - 원래 요청 재시도');

                  // ⭐ localStorage 업데이트 (user 키)
                  localStorage.setItem('user', JSON.stringify(loginResult.data));

                  // 원래 요청 데이터 업데이트
                  const updatedData = {
                    ...data,
                    memberNo: loginResult.data.memberNo,
                    apiKey: loginResult.data.apiKey
                  };

                  // 원래 요청 재시도
                  return apiRequest<T>(endpoint, updatedData, retryCount + 1);
                } else {
                  console.log('[API] ❌ 재인증 실패 - result:', loginResult.result);
                }

              } else {
                console.log('[API] 재인증 HTTP 에러:', loginResponse.status);
              }


            } else {
              console.log('[API] localStorage에 auth 없음');
            }

            console.log('[API] 자동 재인증 실패');

          } catch (retryError) {
            console.error('[API] 재인증 중 오류:', retryError);
          }
        }

        // ⭐ 재시도 실패 또는 재시도 횟수 초과 - 이벤트 발생
        console.log('[API] 인증 만료 - 이벤트 발생 및 로그아웃 처리');
        emitAuthExpired();  // ⭐ 이벤트 발생!

        const error: any = new Error('AUTH_EXPIRED');
        error.isAuthExpired = true;
        throw error;
      }

      const error: any = new Error(errorMessage);
      // ⭐ data를 에러 객체에 추가
      error.code = result.result;
      error.data = result.data;
      throw error;
    }

    return result.data as T;

  } catch (error) {
    throw error;
  }
}




function getErrorMessage(errorCode: number): string {
  const errorMessages: Record<number, string> = {
    1: '서버 오류가 발생했습니다.',
    3: '잘못된 요청 형식입니다.',
    100: '아이디 또는 비밀번호가 일치하지 않습니다.',
    101: '회원가입이 필요합니다.',
    102: '존재하지 않는 사용자입니다.',
    103: '가입되지 않은 이메일입니다.',
    104: '등록되지 않은 전화번호입니다.',
    105: '이미 사용 중인 아이디입니다.',
    106: '이미 사용 중인 전화번호입니다.',
    162: '해당 날짜에 이미 간병일지가 존재합니다.',  // ⭐ 추가
    163: '간병일지 작성일자를 간병기간에 맞게 선택해주세요.',  // ⭐ 추가
    200: '성별을 선택해주세요.',
    201: '환급계좌를 등록해주세요.',
    220: '이미 견적을 작성하셨습니다.',
    222: '주민번호를 등록해주세요.',
    300: '서비스 이용 기록이 없습니다.',
    301: '리뷰 작성 권한이 없습니다.',
    400: '현재 비밀번호가 일치하지 않습니다.',
    999: '알 수 없는 오류가 발생했습니다.',
    1000: '자동 로그인에 실패했습니다.',
  };

  return errorMessages[errorCode] || `오류가 발생했습니다. (코드: ${errorCode})`;
}


// ⭐ 프로필 이미지 업로드 함수 (multipart/form-data)
async function uploadProfileImage(file: File): Promise<UploadProfileData> {
  try {
    const formData = new FormData();
    formData.append('imgFile', file);

    const response = await fetch(`${BASE_URL}/member/uploadProfile.do`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    let responseText = await response.text();

    // ⭐ 1단계: JSON 문자열인 경우 파싱
    if (responseText.startsWith('"') && responseText.endsWith('"')) {
      try {
        responseText = JSON.parse(responseText);
      } catch (unwrapError) {
        console.error('[API] JSON 문자열 언랩 실패:', unwrapError);
      }
    }

    // ⭐ 2단계: 암호화된 응답 복호화
    if (USE_ENCRYPTION && responseText && !responseText.startsWith('{')) {
      try {
        responseText = decryptAES(responseText);
      } catch (decryptError) {
        throw new Error('응답 복호화에 실패했습니다.');
      }
    }

    // ⭐ 3단계: JSON 파싱
    let result: Result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      throw new Error('응답 파싱에 실패했습니다.');
    }

    // ========== 결과 검증 ==========
    if (result.result !== 0) {
      const errorMessage = getErrorMessage(result.result);
      throw new Error(errorMessage);
    }

    return result.data as UploadProfileData;

  } catch (error) {
    throw error;
  }
}

// API Service Objects
export const authApi = {
  login: (req: LoginReq) => apiRequest<LoginData>('/member/login.do', req),

  autoLogin: (req: AutoLoginReq) => apiRequest<LoginData>('/member/autoLogin.do', req),

  logout: (req: LogoutReq) => apiRequest<void>('/member/logout.do', req),

  // ⭐ 휴대폰 인증번호 요청 추가
  phoneCert: (req: PhoneCertReq) => apiRequest<PhoneCertData>('/member/phoneCert.do', req),

  // ⭐ 보호자 회원가입 추가
  regMember: (req: RegMemberReq) => apiRequest<LoginData>('/member/regMember.do', req),

  // ⭐ 간병인 회원가입 추가
  regMedicMember: (req: RegMedicMemberReq) => apiRequest<LoginData>('/member/regMedicMember.do', req),

  // ⭐ 아이디 중복 체크 추가
  checkMemberId: (req: CheckMemberIdReq) => apiRequest<void>('/member/checkMemberId.do', req),

  // ⭐ 네이버 로그인 추가
  naverLogin: (req: NaverLoginReq) => apiRequest<LoginData>('/member/naverLogin.do', req),

  // ⭐ 구글 로그인 추가
  googleLogin: (req: GoogleLoginReq) => apiRequest<LoginData>('/member/googleLogin.do', req),

  // ⭐ 지역 검색 추가
  searchArea: (req: SearchAreaReq) => apiRequest<SearchAreaData>('/member/searchArea.do', req),

  // ⭐ 프로필 이미지 업로드 추가
  uploadProfile: (file: File) => uploadProfileImage(file),

  // 아이디 찾기 - 전화번호 인증번호 발송
  phoneCertFindId: (req: PhoneCertFindIdReq) =>
    apiRequest<PhoneCertFindIdData>('/member/phoneCertFindId.do', req),

  // 아이디 찾기 - 아이디 조회
  findMemberId: (req: FindMemberIdReq) =>
    apiRequest<FindMemberIdData>('/member/findMemberId.do', req),

  // 비밀번호 찾기 - 이메일 존재 확인
  checkMemberIdFindPw: (req: CheckMemberIdFindPwReq) =>
    apiRequest<void>('/member/checkMemberIdFindPw.do', req),

  // 비밀번호 찾기 - 전화번호 인증번호 발송
  phoneCertFindPw: (req: PhoneCertFindPwReq) =>
    apiRequest<PhoneCertFindPwData>('/member/phoneCertFindPw.do', req),

  // 비밀번호 변경
  changeMemberPw: (req: ChangeMemberPwReq) =>
    apiRequest<void>('/member/changeMemberPw.do', req),
};

// API 함수 추가 (api.ts 하단)
export const mainApi = {
  // 보호자 메인화면
  gdMainView: (req: MainViewReq) =>
    apiRequest<GuardianMainViewData>('/care/gdMainView.do', req),

  // 간병인 메인화면
  mdMainView: (req: MainViewReq) =>
    apiRequest<CaregiverMainViewData>('/care/mdMainView.do', req),
};

export const profileApi = {
  // 보호자 프로필 조회
  getGuardianProfile: (req: ProfileRequest) =>
    apiRequest<GuardianProfile>('/member/gdMyproMain.do', req),

  // 간병인 프로필 조회
  getCaregiverProfile: (req: ProfileRequest) =>
    apiRequest<CaregiverProfile>('/member/mdMyproMain.do', req),

  outMemberAct: (req: OutMemberActReq) =>
    apiRequest<void>('/member/outMemberAct.do', req),

  // 보호자 프로필 상세 조회
  getGuardianDetail: (req: ProfileRequest) =>
    apiRequest<GdMemberPrDetailData>('/member/gdMemberPrDetail.do', req),

  // 간병인 프로필 상세 조회
  getCaregiverDetail: (req: ProfileRequest) =>
    apiRequest<MdMemberPrDetailData>('/member/mdMemberPrDetail.do', req),

  // 보호자 프로필 수정
  updateGuardianProfile: (req: ModGdMemberPrReq) =>
    apiRequest<void>('/member/modGdMemberPr.do', req),

  // 간병인 프로필 수정
  updateCaregiverProfile: (req: ModMdMemberPrReq) =>
    apiRequest<void>('/member/modMdMemberPr.do', req),

  // 비밀번호 변경
  changePassword: (req: ChangeMyProPwReq) =>
    apiRequest<void>('/member/changeMyProPw.do', req),

  // 연락처 변경 - 인증번호 요청
  requestPhoneChange: (req: PhoneCertChangePhoneNumReq) =>
    apiRequest<PhoneCertChangePhoneNumData>('/phoneCertChangePhoneNum.do', req),

  // 연락처 변경 - 최종 변경
  changePhoneNumber: (req: ChangePhoneNumReq) =>
    apiRequest<void>('/changePhoneNum.do', req),
};

//간병관련
export const careApi = {
  // 간병 요청 생성
  createRequest: (req: CareRequestReq) =>
    apiRequest<CareRequestData>('/care/crgvrNewRqst.do', req),
  // ⭐ 간병 요청 상세 조회
  getRequestDetail: (req: CareRequestDetailReq) =>
    apiRequest<CareRequestDetailData>('/care/crgvrRqstDetail.do', req),

  // ⭐ 진행중 간병 상세 조회 (새로 추가)
  getGoingCareDetail: (req: GoingCareDetailReq) =>
    apiRequest<GoingCareDetailData>('/care/goingCareDetail.do', req),

  // ⭐ 보호자 간병 요청 목록 (새로 추가)
  getGdCareList: (req: GdCareListReq) =>
    apiRequest<GdCareListData>('/care/gdCareList.do', req),

  // ⭐ 진행중 간병 상세 (간병인) - 추가
  getMedicGoingCareDetail: (req: CareReq) =>
    apiRequest<MedicGoingCareDetailData>('/care/medicGoingCareDetail.do', req),

  // ⭐ 간병인 정산 요청 - 추가
  medicReqCalcYn: (req: CareReq) =>
    apiRequest<void>('/care/medicReqCalcYn.do', req),

  // ⭐ 보호자 정산 승인 - 추가
  gdCalcYn: (req: CareReq) =>
    apiRequest<void>('/care/gdCalcYn.do', req),

  // ⭐ 간병 요청 취소(삭제)
  delCareReq: (req: DelCareReqReq) =>
    apiRequest<void>('/care/delCareReq.do', req),

  // ⭐ 간병인 내 간병 목록 (선택된 견적 기준)
  getMedicCareChoList: (req: MedicCareChoListReq) =>
    apiRequest<MedicCareChoListData>('/care/medicCareChoList.do', req),

  // ⭐ 간병 공고 수정
  modifyRequest: (req: ModifyCareRequestReq) =>
    apiRequest<void>('/care/crgvrModRqst.do', req),

};



export const estimateApi = {
  // 견적 작성
  write: (req: EstimateWriteReq) =>
    apiRequest<EstimateWriteRsp>('/estimate/estimateNewWrite.do', req),

  // 견적 상세 조회
  detail: (req: EstimateDetailReq) =>
    apiRequest<EstimateDetailData>('/estimate/estimateDetail.do', req),

  // 견적 수정
  update: (req: EstimateUpdateReq) =>
    apiRequest<EstimateWriteRsp>('/estimate/medicEstimateNewMod.do', req),

  // 견적 삭제
  delete: (req: EstimateDeleteReq) =>
    apiRequest<void>('/estimate/estimateDelete.do', req),

  // 결제 가능 체크
  mdPayCheck: (req: MdPayCheckReq) =>
    apiRequest<void>('/estimate/mdPayCheck.do', req),

  // ⭐ 받은 견적 목록 추가
  getEstimateList: (req: LstesRcvdReq) =>
    apiRequest<LstesRcvdData>('/estimate/lstesRcvd.do', req),

  // ⭐ 보낸 견적 목록 (간병인)
  getSentQuotes: (req: MemberPagingReq) =>
    apiRequest<RgEstimateListData>('/member/rgEstimateList.do', req),
};

// ⭐ reviewApi
export const reviewApi = {
  // 간병인 리뷰 목록 조회
  getMedicReviewList: (req: MedicReviewListReq) =>
    apiRequest<MedicReviewListData>('/review/medicReviewList.do', req),
  // 간병인 리뷰 목록 (보호자 → 특정 간병인)
  getMedicMemberDetailReviewList: (req: MedicMemberDetailReviewListReq) =>
    apiRequest<MedicMemberDetailReviewListData>('/member/medicMemberdetailReviewList.do', req),

  // 리뷰 등록
  regReview: (req: RegReviewReq) =>
    apiRequest<void>('/review/regReview.do', req),

  // 리뷰 수정
  modReview: (req: ModReviewReq) =>
    apiRequest<void>('/review/modReview.do', req),

  // 리뷰 삭제
  deleteReview: (req: DeleteReviewReq) =>
    apiRequest<void>('/review/deleteReview.do', req),

  // 리뷰 상세 조회 (수정용)
  detailReview: (req: DetailReviewReq) =>
    apiRequest<DetailReviewData>('/review/detailReview.do', req),
};


// ⭐ paidApi
export const paidApi = {
  // 환급계좌 조회
  detailMdBank: (req: DetailMdBankReq) =>
    apiRequest<DetailMdBankData>('/paid/detailMdBank.do', req),

  // 환급계좌 등록/수정/삭제
  modPaidBankInfo: (req: ModPaidBankInfoReq) =>
    apiRequest<void>('/paid/modPaidBankInfo.do', req),

  // 정산 목록 조회
  getPaidList: (req: PaidListReq) =>
    apiRequest<PaidListData>('/paid/paidList.do', req),

  // 정산 상세 조회
  getPaidDetail: (req: PaidDetailReq) =>
    apiRequest<PaidDetailData>('/paid/paidDetail.do', req),
};

// ==================== API 함수 추가 (기존 파일 하단에 추가) ====================
export const notiApi = {
  // 알림 목록 조회
  getList: (req: NotiListReq) =>
    apiRequest<NotiListData>('/noti/notiList.do', req),

  // 알림 읽음 처리 (단일)
  checkOne: (req: NotiCheckOneReq) =>
    apiRequest<void>('/noti/notiCheckOne.do', req),

  // 알림 전체 읽음 처리
  checkGroup: (req: NotiCheckGroupReq) =>
    apiRequest<void>('/noti/notiCheckGroup.do', req),

  // 내 알림 체크 (읽지 않은 알림 있는지)
  checkMyNoti: (req: NotiCheckGroupReq) =>
    apiRequest<CheckMyNotiData>('/noti/checkMyNoti.do', req),
};


// ==================== CARE NOTE API ====================
export const careNoteApi = {
  // 간병일지 목록 조회
  getList: (req: CareNoteListReq) =>
    apiRequest<CareNoteListData>('/care/careNoteList.do', req),

  // ⭐ 간병인용 간병일지 목록 추가
  getMedicList: (req: CareNoteListReq) =>
    apiRequest<CareNoteListData>('/care/medicCareNoteList.do', req),

  // 간병일지 상세 조회 (보호자용)
  getDetail: (req: CareNoteDetailReq) =>
    apiRequest<CareNoteDetailFullData>('/care/careNoteDetail.do', req),

  // 간병일지 상세 조회 (간병인용)
  getMedicDetail: (req: CareNoteDetailReq) =>
    apiRequest<CareNoteDetailFullData>('/care/medicCareNoteDetail.do', req),

  // 간병일지 작성
  write: (req: CareNoteWriteReq) =>
    apiRequest<CareNoteWriteData>('/care/regCareNote.do', req),

  // 간병일지 수정
  update: (req: CareNoteUpdateReq) =>
    apiRequest<void>('/care/modCareNote.do', req),

  // 간병일지 삭제
  delete: (req: CareNoteDeleteReq) =>
    apiRequest<void>('/care/deleteCareNote.do', req),

  // 간병일지 파일 업로드
  uploadFile: (file: File, fileType: 'image' | 'signature', careNo?: number) =>
    uploadCareNoteFile(file, fileType, careNo),

  // 간병인 진행중인 간병 목록 (간병일지 작성용)
  getMedicGoingCareList: (req: MedicCareGoingListReq) =>
    apiRequest<MedicCareGoingListData>('/care/medicCareNoteList.do', req),
};


// ==================== API 함수 ====================

export const certificateApi = {
  // 증명서 목록 조회
  getCertificateList: (req: GetCertificateListReq) =>
    apiRequest<GetCertificateListData>('/care/getCertificateList.do', req),

  // 증명서 다운로드 URL 생성
  getCertificateDownloadUrl: (certificateNo: number) =>
    `${BASE_URL}/care/downloadCertificate.do?certificateNo=${certificateNo}`,

  // ⭐ 일괄 다운로드 URL 생성
  getAllCertificatesDownloadUrl: (careNo: number, ptName?: string) => {
    let url = `${BASE_URL}/care/downloadAllCertificates.do?careNo=${careNo}`;
    if (ptName) {
      url += `&ptName=${encodeURIComponent(ptName)}`;
    }
    return url;
  },
};


// ==================== 간병인 일감 API ====================
export const jobApi = {
  // 일감 찾기 목록
  search: (req: MemberPagingReq) =>
    apiRequest<JobSearchData>('/care/jobSearch.do', req),

  // 일감 상세
  getDetail: (req: CareReq) =>
    apiRequest<JobSearchDetailData>('/care/jobSearchNewDetail.do', req),

  // 견적 상세 조회 (수정용)
  getEstimateDetail: (req: EstimateReq) =>
    apiRequest<MedicEstimateDetailData>('/estimate/medicEstimateDetail.do', req),
};


export const inquiryApi = {
  regInquiry: (req: RegInquiryReq) =>
    apiRequest<void>('/inquiry/regInquery.do', req),
};

// ==================== FAQ API ====================
export const faqApi = {
  // FAQ 목록 조회
  getList: (req: FaqListReq) =>
    apiRequest<FaqListData>('/faq/faqList.do', req),

  // FAQ 상세 URL 생성 (WebView용)
  getDetailUrl: (faqNo: number) =>
    `${BASE_URL}/mngr/appmngr/faqDetail.do?faqNo=${faqNo}`,
};

// 공지사항 API 함수 추가
export const notifyApi = {
  getList: (req: NotifyListReq) => apiRequest('/notify/notifyList.do', req),
  getDetailUrl: (notifyNo: number) => `${BASE_URL}/mngr/appmngr/notifyDetail.do?notifyNo=${notifyNo}`,
};


export const attendApi = {
  // 활성 간병 목록 조회 (지오펜스용)
  getActiveCareList: (req: { memberNo: number; apiKey: string }) =>
    apiRequest<ActiveCareListData>('/attend/active-list', req),

  // 출근 처리
  checkIn: (req: CheckInReq & { memberNo: number; apiKey: string }) =>
    apiRequest<CheckInData>('/attend/check-in', req),

  // 퇴근 처리
  checkOut: (req: CheckOutReq & { memberNo: number; apiKey: string }) =>
    apiRequest<CheckOutData>('/attend/check-out', req),

  // 출퇴근 이력 조회
  getHistory: (req: AttendHistoryReq & { memberNo: number; apiKey: string }) =>
    apiRequest<AttendHistoryData>('/attend/history', req),

  // 오늘 출퇴근 기록 조회
  getTodayAttend: (req: TodayAttendReq & { memberNo: number; apiKey: string }) =>
    apiRequest<TodayAttendData>('/attend/today', req),
};

// API 함수 추가
export const memberApi = {
  // 간병인 이용내역 조회
  getUseMdList: (req: MemberUseMdListReq) =>
    apiRequest<MemberUseMdListData>('/member/memberUseMdList.do', req),

  // ⭐ 간병 내역 조회 (간병인) - 추가
  getRunCareList: (req: MemberRunCareListReq) =>
    apiRequest<MemberRunCareListData>('/member/memberRunCareList.do', req),
};